import type { ReactNode } from "react";
import { Card, Title, Subtitle, DonutChart, AreaChart, Legend } from "@tremor/react";
import { Globe2, AlertTriangle } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { deriveExportSplit, deriveExportTrend } from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { eur, formatLunaKey, procent, procentAxis } from "@/lib/format";

const LABEL: Record<string, string> = { export: "Export", intern: "Intern (RO)" };

export function ExportSplitSection() {
  useFilterVersion();
  const split = deriveExportSplit();
  const trend = deriveExportTrend().map((r) => ({
    luna: formatLunaKey(r.luna_key),
    "Export % venit": r.export_pct_venit,
  }));

  const donutData = split.map((r) => ({ segment: LABEL[r.segment] ?? r.segment, venit_eur: r.venit_eur }));
  const exportRow = split.find((r) => r.segment === "export");
  const percepiePct = 60;
  const realitatePct = exportRow?.pondere_venit_pct ?? 0;

  return (
    <section>
      <SectionHeader
        id="export"
        icon={Globe2}
        titlu="5. Export % vs. intern % (pe venit)"
        descriere='Percepția clientului: „60% export". Reperul măsurat, pe venit facturat.'
      />

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Pe datele mock, ponderea export e <strong>{procent(realitatePct)}</strong> din venit — vs.
          percepția de <strong>{percepiePct}%</strong>. Cifra reală (așteptare ~15%) se confirmă abia pe
          datele WinMentor.
        </span>
      </div>

      <Grid2>
        <Card className="flex flex-col ring-slate-200">
          <Title>Split venit — Export vs. Intern</Title>
          <Subtitle>Pondere calculată pe valoare_vânzare, nu pe nr. comenzi</Subtitle>
          <DonutChart
            className="mt-4 h-60 w-full"
            data={donutData}
            category="venit_eur"
            index="segment"
            colors={["blue", "slate"]}
            valueFormatter={(v) => eur(v, 0)}
            noDataText="Fără date"
          />
          <Legend
            className="mt-3 justify-center"
            categories={donutData.map((d) => d.segment)}
            colors={["blue", "slate"]}
          />
          <div className="mt-4 grid grid-cols-2 gap-3">
            {split.map((r) => (
              <div key={r.segment} className="flex min-w-0 flex-col rounded-lg border border-slate-200/70 bg-slate-50 p-3">
                <p className="min-w-0 truncate text-xs font-medium text-slate-500">{LABEL[r.segment]}</p>
                <p className="tabular mt-1 text-xl font-semibold tracking-tight text-slate-900">
                  {procent(r.pondere_venit_pct)}
                </p>
                <p className="tabular mt-1 truncate text-xs text-slate-400">
                  {r.nr_comenzi} comenzi · marjă {procent(r.marja_pct)}
                </p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="flex flex-col ring-slate-200">
          <Title>Evoluție lunară export % (din venit)</Title>
          <Subtitle>Trend — dacă ponderea reală crește sau scade față de percepție</Subtitle>
          <AreaChart
            className="mt-4 h-60 w-full"
            data={trend}
            index="luna"
            categories={["Export % venit"]}
            colors={["blue"]}
            valueFormatter={procentAxis}
            showLegend={false}
            curveType="monotone"
            yAxisWidth={48}
            noDataText="Fără date"
          />
        </Card>
      </Grid2>
    </section>
  );
}

// Grid 2 coloane la md+, 1 coloană pe mobil — carduri de înălțime egală.
function Grid2({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">{children}</div>;
}
